import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageFarmProfiles({ navigate }) {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">Farm Profiles</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        A farm profile is a named copy of how your FARM tabs are set up &mdash; what to pick up,
        what to open, what to sell and melt, your item rules and your locked bag slots. Make one for
        each way you farm (a bloodshed run, a boss room, a selling trip) and switch between them
        with one click. Everything is on the <strong className="text-white">FARM &rsaquo; Profiles</strong> tab.
      </p>

      <Callout type="info">
        <strong>Loading a profile never switches anything on.</strong> A profile tells the bot{' '}
        <em>what</em> to do. <em>Whether</em> it does it is still up to you. Selling will not start
        just because you loaded a selling profile.
      </Callout>

      <DocHeading>Saving a Profile</DocHeading>
      <Step n={1} title="Set the tabs up first">
        Set up Loot, Gather, Sell, Melt, Item lists and Bag the way you want them for this kind of
        farming. The profile copies what is set at the moment you save.
      </Step>
      <Step n={2} title="Type a name">
        Use the box under <strong>Saved profiles</strong> (it reads{' '}
        <em>type a name to save a new profile</em>). The small button next to it clears the box.
      </Step>
      <Step n={3} title="Press SAVE">
        The profile appears in the list and <strong>Now using</strong> switches to it. If a
        profile with that name already exists, the button reads <strong>OVERWRITE</strong> instead
        &mdash; pressing it replaces the saved one with your current setup.
      </Step>

      <Callout type="warning">
        A profile name cannot contain <code>\ / : * ? &quot; &lt; &gt; |</code>, and it cannot start
        with a space or end with a space or a dot. If the name will not work, the card tells you in
        orange before you press anything.
      </Callout>

      <Shot
        name="farm-profiles-01-profiles-tab.png"
        tab="FARM › Profiles"
        caption="Four saved profiles, Bloodshed run in use; on the right what a profile carries and what stays as you set it."
        shoot="menushot (stage farmprofile, 4 .farm fájl): a FARM › Profiles lap — Now using Bloodshed run, Saved profiles (Bloodshed run, Key farm, PW boss room, Selling trip), a név-mező, OVERWRITE / LOAD / DELETE, a zár-figyelmeztető sor; jobbra WHAT A PROFILE CARRIES (Included, Stays as you set it)."
      />

      <DocHeading>Loading and Deleting</DocHeading>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">Now using</strong>, 'The profile your current setup came from (the last one you loaded or saved), or - if none or if that profile is gone. That profile is selected when you open the tab.'],
          [<strong className="text-white">Saved profiles</strong>, 'Click a profile to select it. Its name is copied into the name box.'],
          [<strong className="text-white">LOAD</strong>, 'Applies the selected profile. If that would unlock a bag slot, it asks first (see below).'],
          [<strong className="text-white">SAVE / OVERWRITE</strong>, 'Saves your current setup under the name in the box. OVERWRITE means a profile with that name already exists.'],
          [<strong className="text-white">DELETE</strong>, 'Turns into a red REALLY DELETE. Press that to remove the selected profile for good.'],
        ]}
      />
      <Callout type="info">
        <strong>Now using is where your setup came from, not a live link.</strong> If you change a
        setting after loading, the profile itself stays as it was. To update it, select it and
        press <strong>OVERWRITE</strong>.
      </Callout>

      <DocHeading>What a Profile Carries</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The <strong className="text-white">WHAT A PROFILE CARRIES</strong> card lists it exactly.
        Loading changes the first group and leaves the second alone.
      </p>
      <DocTable
        headers={['Included', 'What comes along']}
        rows={[
          [<button onClick={() => navigate('auto-loot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Loot</button>, 'range, filter, pace'],
          [<button onClick={() => navigate('auto-sell')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Sell</button>, 'what and when'],
          [<button onClick={() => navigate('auto-gather')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Gather</button>, 'what to open, range'],
          [<button onClick={() => navigate('auto-melt')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Melt</button>, 'what to melt, cubes'],
          [<button onClick={() => navigate('item-lists')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Item lists</button>, 'every rule'],
          [<button onClick={() => navigate('bag')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Bag</button>, 'locked slots - asks before unlocking'],
        ]}
      />
      <DocTable
        headers={['Stays as you set it', 'Why']}
        rows={[
          [<strong className="text-white">On / off switches</strong>, 'Loading never starts selling (or looting, gathering, melting).'],
          [<strong className="text-white">Dry run</strong>, 'The safety brake stays where you left it.'],
          [<strong className="text-white">Keys</strong>, 'Your key bindings are yours, not part of a way of farming.'],
        ]}
      />
      <Callout type="info">
        Only these FARM tabs are in a farm profile. Bloodshed, your routes and everything outside
        FARM are not. Combat keeps its own profiles &mdash; see{' '}
        <button onClick={() => navigate('auto-combat')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Combat</button>.
      </Callout>

      <DocHeading>Bag Locks: Why LOAD Sometimes Asks</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        After loading, <em>exactly</em> the profile&rsquo;s slots are locked &mdash; so switching
        between two profiles really switches, instead of locks piling up. Adding a lock never asks:
        the worst it can do is keep an item you meant to sell. Removing one is different. The profile
        knows which slots were locked when you saved it, not what is sitting in them now, and an
        unlocked slot can be sold or melted again.
      </p>
      <Step n={1} title="Press LOAD">
        If nothing would be unlocked, the profile loads straight away.
      </Step>
      <Step n={2} title="If slots would be unlocked, nothing changes yet">
        The button turns into an orange <strong>REALLY LOAD</strong>, and the card says how many
        bag slots loading would unlock. Clicking another profile or another button cancels the
        question.
      </Step>
      <Step n={3} title="Check, then press REALLY LOAD">
        Look at <strong>FARM &rsaquo; Bag</strong> first if you are not sure what is in those slots.
        If you change a lock in the meantime, REALLY LOAD does not go ahead: it asks again with the
        new number.
      </Step>

      <DocHeading>The Item List Comes Along</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Saving a profile copies your item rules into it, and also saves them for you, the same as
        pressing SAVE on{' '}
        <button onClick={() => navigate('item-lists')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Item lists</button>.
        Loading replaces your rules with the profile&rsquo;s.
      </p>
      <Callout type="warning">
        <strong>A profile saved without item rules clears yours when loaded.</strong> That is
        deliberate: the profile remembers that there was no list, so another profile&rsquo;s rules
        cannot linger. Save the profile again once you have the rules you want.
      </Callout>

      <DocHeading>Messages</DocHeading>
      <DocTable
        headers={['Message', 'What it means']}
        rows={[
          [<strong className="text-white">Loaded &ldquo;name&rdquo; - bag: 2 slots locked, 1 unlocked.</strong>, 'Done. The numbers say how your bag locks changed.'],
          [<strong className="text-white">&hellip; - it has no bag locks, the bag was left as it is</strong>, 'The profile has no lock information, so your current locks were not touched.'],
          [<strong className="text-white">Pick a profile from the list first.</strong>, 'LOAD and DELETE need a selected profile.'],
          [<span className="text-amber-300">Type a name for the profile first.</span>, 'SAVE was pressed with an empty (or unusable) name.'],
          [<span className="text-amber-300">Your bag locks changed since you pressed LOAD &hellip;</span>, 'You changed a lock while REALLY LOAD was waiting. The message gives the new number; press REALLY LOAD again if that is still what you want.'],
          [<span className="text-amber-300">Could not read &ldquo;name&rdquo;.</span>, 'The profile file could not be read. Nothing was changed.'],
          [<strong className="text-white">Still working - press it again.</strong>, 'The previous save or load has not finished yet. Press the button once more.'],
          [<strong className="text-white">&ldquo;name&rdquo; is no longer in the folder.</strong>, 'The profile was removed outside the bot. The list refreshes by itself.'],
          [<span className="text-amber-300">Saved &ldquo;name&rdquo;, but the item list could not be copied</span>, 'Settings and locks were saved, the item rules were not. Try saving again.'],
          [<span className="text-amber-300">&hellip;, but its item list could not be copied - the old list is still in use.</span>, 'The settings loaded, but your previous item rules are still active.'],
          [<span className="text-amber-300">The list is full - not everything is shown.</span>, 'You have more profiles than the list can show. Delete the ones you no longer use.'],
        ]}
      />
      <Callout type="info">
        Profiles are stored on this PC and shared by every character you play on it.
      </Callout>
    </div>
  );
}

export default PageFarmProfiles;
